/**
 * RealTimeVoice - Interactive Voice AI for AI-SDR
 * 
 * Features:
 * - Browser-based speech-to-speech conversation
 * - Phone call with AI conversation
 * - Voicemail drop
 * - Model switching (AWS Nova 2 Sonic / GCP coming soon)
 * - Live transcript display
 * - Interrupt handling
 */

import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { AuthContext } from "../AuthProvider";
import InteractiveVoiceService, { PROVIDERS, DEFAULT_PROMPTS } from "./services/InteractiveVoiceService";
import RealTimeVoiceService from "./services/RealTimeVoiceService";
import VoiceMailService from "./services/VoiceMailService";
import {
  FaPhone,
  FaSpinner,
  FaCheck,
  FaMicrophone,
  FaMicrophoneSlash,
  FaPhoneSlash,
  FaVoicemail,
  FaComments,
  FaUserTie,
  FaHandPaper,
  FaPlay,
  FaFileAlt,
  FaRobot,
  FaVolumeUp,
  FaDesktop,
  FaCog,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { collection, query, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";

// Call modes
const CALL_MODES = [
  { 
    id: "browser", 
    name: "Browser Voice", 
    description: "Real-time AI conversation via microphone",
    icon: FaDesktop,
    color: "emerald"
  },
  { 
    id: "realtime", 
    name: "Interactive Phone Call", 
    description: "AI-powered phone conversation",
    icon: FaComments,
    color: "purple"
  },
  { 
    id: "verification", 
    name: "Employment Verification", 
    description: "Verify employment dates, title, status",
    icon: FaUserTie,
    color: "blue"
  },
  { 
    id: "voicemail", 
    name: "Leave Voicemail", 
    description: "Deliver message if no answer",
    icon: FaVoicemail,
    color: "orange"
  },
];

const RealTimeVoice = ({ selectedTeam }) => {
  const { user } = useContext(AuthContext);

  // Theme state (default to dark)
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("ai-sdr-voice-theme") || "dark";
    }
    return "dark";
  });
  const isDark = theme === "dark";

  // Provider state
  const [selectedProvider, setSelectedProvider] = useState("AWS");
  const providerConfig = PROVIDERS[selectedProvider];

  // Mode state
  const [selectedMode, setSelectedMode] = useState("browser");
  const [selectedVoice, setSelectedVoice] = useState(providerConfig?.voices?.[0]?.id || "tiffany");

  // Prompt state
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");

  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);

  // Prospect state (for phone modes)
  const [prospects, setProspects] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [voicemailScript, setVoicemailScript] = useState("");

  // Browser voice state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [sessionTime, setSessionTime] = useState(0);

  // Phone call state
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Transcript state
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);

  // Service refs
  const voiceServiceRef = useRef(null);
  const sessionTimerRef = useRef(null);
  const callTimerRef = useRef(null);
  const pollRef = useRef(null);

  // Error state
  const [error, setError] = useState(null);

  // Auto-scroll transcripts
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Session timer for browser voice
  useEffect(() => {
    if (isConnected) {
      sessionTimerRef.current = setInterval(() => {
        setSessionTime((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(sessionTimerRef.current);
      setSessionTime(0);
    }
    return () => clearInterval(sessionTimerRef.current);
  }, [isConnected]);

  // Call timer for phone calls
  useEffect(() => {
    if (activeCall) {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(callTimerRef.current);
      setCallDuration(0);
    }
    return () => clearInterval(callTimerRef.current);
  }, [activeCall]);

  // Load prospects
  useEffect(() => {
    loadProspects();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (voiceServiceRef.current) {
        voiceServiceRef.current.disconnect();
      }
      clearInterval(pollRef.current);
    };
  }, []);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem("ai-sdr-voice-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  const loadProspects = async () => {
    setLoadingProspects(true);
    try {
      const contactsRef = collection(db, "contacts");
      const q = query(contactsRef, orderBy("fullName"), limit(100));
      const snapshot = await getDocs(q);
      const prospectData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProspects(prospectData);
    } catch (error) {
      console.error("Error loading prospects:", error);
    } finally {
      setLoadingProspects(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const getModeColor = (color) => {
    const colors = {
      emerald: { bg: isDark ? "bg-emerald-500/20" : "bg-emerald-50", border: "border-emerald-500", text: "text-emerald-500" },
      purple: { bg: isDark ? "bg-purple-500/20" : "bg-purple-50", border: "border-purple-500", text: "text-purple-500" },
      blue: { bg: isDark ? "bg-blue-500/20" : "bg-blue-50", border: "border-blue-500", text: "text-blue-500" },
      orange: { bg: isDark ? "bg-orange-500/20" : "bg-orange-50", border: "border-orange-500", text: "text-orange-500" },
    };
    return colors[color] || colors.emerald;
  };

  // Browser voice connection
  const connectBrowserVoice = async () => {
    setIsConnecting(true);
    setError(null);
    setMessages([{ role: "system", text: "🎤 Connecting...", timestamp: new Date().toISOString() }]);

    try {
      const service = new InteractiveVoiceService();
      voiceServiceRef.current = service;

      // Setup event listeners
      service.on("connected", () => {
        console.log("[RealTimeVoice] Connected to voice service");
      });

      service.on("disconnected", () => {
        setIsConnected(false);
        setIsSpeaking(false);
        setIsAiSpeaking(false);
      });

      service.on("error", (data) => {
        setError(data.message);
        toast.error(data.message);
      });

      service.on("transcript", (msg) => {
        setMessages((prev) => {
          const recentMessages = prev.slice(-3);
          const isDuplicate = recentMessages.some(
            (m) => m.text === msg.text && m.role === msg.role
          );
          if (isDuplicate) return prev;
          return [...prev, msg];
        });
      });

      service.on("audioLevel", (level) => {
        setAudioLevel(level);
      });

      service.on("userSpeakingChange", (speaking) => {
        setIsSpeaking(speaking);
      });

      service.on("aiSpeakingChange", (speaking) => {
        setIsAiSpeaking(speaking);
      });

      const systemPrompt = customPrompt || DEFAULT_PROMPTS[selectedMode] || DEFAULT_PROMPTS.realtime;
      await service.connect(systemPrompt, selectedProvider);

      setIsConnected(true);
      setIsConnecting(false);
      setMessages([{ role: "system", text: "🎤 Connected! Start speaking...", timestamp: new Date().toISOString() }]);

    } catch (err) {
      console.error("[RealTimeVoice] Session error:", err);
      setError(err.message);
      setIsConnecting(false);
      toast.error(err.message || "Failed to connect");
    }
  };

  const disconnectBrowserVoice = useCallback(() => {
    if (voiceServiceRef.current) {
      voiceServiceRef.current.disconnect();
      voiceServiceRef.current = null;
    }
    setIsConnected(false);
    setIsSpeaking(false);
    setIsAiSpeaking(false);
    setAudioLevel(0);
  }, []);

  // Phone call functions
  const startPhoneCall = async () => {
    const phoneToCall = phoneNumber.trim() || selectedProspect?.phone;
    
    if (!phoneToCall || phoneToCall.length < 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    // For voicemail mode, require a script
    if (selectedMode === "voicemail" && !voicemailScript.trim()) {
      setError("Please enter a voicemail script");
      return;
    }

    setIsLoading(true);
    setError(null);
    setCallStatus("Initiating call...");
    setMessages([]);

    try {
      const service = new InteractiveVoiceService();
      service.provider = selectedProvider;
      voiceServiceRef.current = service;

      const systemPrompt = customPrompt || DEFAULT_PROMPTS[selectedMode] || DEFAULT_PROMPTS.realtime;
      
      const result = await service.initiatePhoneCall({
        phoneNumber: phoneToCall.startsWith("+") ? phoneToCall : `+1${phoneToCall}`,
        mode: selectedMode === "voicemail" ? "nova_voicemail" : selectedMode,
        voiceId: selectedVoice,
        systemPrompt,
        voicemailScript: selectedMode === "voicemail" ? voicemailScript : undefined,
      });

      setActiveCall(result.callSid);
      setCallStatus("✅ Call initiated - connecting...");
      
      setMessages([{
        id: Date.now(),
        role: "system",
        text: `Call initiated to ${phoneToCall} (${CALL_MODES.find(m => m.id === selectedMode)?.name})`,
        timestamp: new Date().toISOString(),
      }]);

      // Start polling for transcripts
      startCallPolling(result.callSid);
      
    } catch (err) {
      console.error("Call error:", err);
      setError(err.message || "Failed to initiate call");
      setCallStatus(null);
      toast.error(err.message || "Failed to initiate call");
    } finally {
      setIsLoading(false);
    }
  };

  const startCallPolling = (callSid) => {
    pollRef.current = setInterval(async () => {
      try {
        const service = voiceServiceRef.current;
        if (!service) return;

        const data = await service.getCallStatus(callSid);
        if (data.transcripts) {
          setMessages(data.transcripts);
        }
        if (data.status === "completed" || data.status === "failed") {
          setActiveCall(null);
          setCallStatus(`Call ${data.status}`);
          clearInterval(pollRef.current);
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 1000);
  };

  const endPhoneCall = async () => {
    if (activeCall && voiceServiceRef.current) {
      try {
        await voiceServiceRef.current.endPhoneCall(activeCall);
      } catch (err) {
        console.error("Error ending call:", err);
      }
    }
    clearInterval(pollRef.current);
    setActiveCall(null);
    setCallStatus("Call ended");
  };

  const isPhoneMode = selectedMode !== "browser";
  const isActive = isConnected || activeCall;

  return (
    <div className={`min-h-screen ${
      isDark 
        ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" 
        : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"
    }`}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className={`rounded-xl p-5 mb-6 border ${
          isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-lg"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                <FaPhone className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Real-Time Voice</h1>
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  AI-powered voice conversations & calls
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2.5 rounded-lg transition-colors ${
                  showSettings
                    ? isDark 
                      ? "bg-emerald-500/20 text-emerald-400" 
                      : "bg-emerald-100 text-emerald-600"
                    : isDark 
                      ? "bg-slate-700/50 hover:bg-slate-600/50 text-slate-400" 
                      : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }`}
                title="Settings"
              >
                <FaCog className={showSettings ? "animate-spin-slow" : ""} />
              </button>
              <button
                onClick={toggleTheme}
                className={`p-2.5 rounded-lg transition-colors ${
                  isDark 
                    ? "bg-slate-700/50 hover:bg-slate-600/50 text-yellow-400" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }`}
                title="Toggle theme"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className={`rounded-xl p-4 mb-6 border ${
            isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                <FaCog className="inline mr-2" />
                Advanced Settings
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
              >
                Close
              </button>
            </div>
            
            {/* Provider Selector */}
            <div className="mb-3">
              <label className={`block text-xs font-medium mb-2 ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                AI Speech Model Provider
              </label>
              <div className="flex gap-2">
                {Object.entries(PROVIDERS).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => !config.disabled && setSelectedProvider(key)}
                    disabled={config.disabled || isActive}
                    className={`flex-1 p-2 rounded-lg text-center transition-all border text-xs ${
                      selectedProvider === key
                        ? "bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500"
                        : config.disabled
                        ? isDark
                          ? "bg-slate-900/50 border-slate-700 opacity-50 cursor-not-allowed"
                          : "bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed"
                        : isDark
                        ? "bg-slate-900 border-slate-600 hover:border-slate-500"
                        : "bg-gray-50 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                      {key}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                      {config.disabled ? "Coming Soon" : config.name.split(" ").slice(-2).join(" ")}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
              These settings affect the AI model used for voice conversations.
            </p>
          </div>
        )}

        {/* Main Grid */}
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: Configuration (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Mode Selection */}
            <div className={`rounded-xl p-4 border ${
              isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"
            }`}>
              <label className={`block text-sm font-medium mb-3 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                Conversation Mode
              </label>
              <div className="space-y-2">
                {CALL_MODES.map((mode) => {
                  const colors = getModeColor(mode.color);
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setSelectedMode(mode.id)}
                      disabled={isActive}
                      className={`w-full p-3 rounded-lg text-left transition-all border-2 disabled:opacity-50 ${
                        selectedMode === mode.id
                          ? `${colors.bg} ${colors.border}`
                          : isDark
                            ? "bg-slate-900 border-slate-700 hover:border-slate-600"
                            : "bg-gray-50 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <mode.icon className={`${selectedMode === mode.id ? colors.text : isDark ? "text-slate-400" : "text-gray-500"}`} />
                        <div className="flex-1">
                          <div className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}>{mode.name}</div>
                          <div className={`text-xs ${isDark ? "text-slate-500" : "text-gray-500"}`}>{mode.description}</div>
                        </div>
                        {selectedMode === mode.id && <FaCheck className={colors.text} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Phone Number (for phone modes) */}
            {isPhoneMode && (
              <div className={`rounded-xl p-4 border ${
                isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"
              }`}>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                    Phone Number
                  </label>
                  <div className="relative">
                    <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDark ? "text-slate-500" : "text-gray-400"}`}>+1</span>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                      disabled={isActive}
                      placeholder="Enter phone number"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-lg font-mono text-sm outline-none border transition-all disabled:opacity-50 ${
                        isDark
                          ? "bg-slate-900 border-slate-600 focus:border-emerald-500 text-white"
                          : "bg-gray-50 border-gray-200 focus:border-emerald-500"
                      }`}
                    />
                  </div>
                </div>

                {/* Voice Selection */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                    AI Voice
                  </label>
                  <div className="flex gap-2">
                    {(providerConfig?.voices || []).map((voice) => (
                      <button
                        key={voice.id}
                        onClick={() => setSelectedVoice(voice.id)}
                        disabled={isActive}
                        className={`flex-1 p-2 rounded-lg text-center transition-all border text-sm disabled:opacity-50 ${
                          selectedVoice === voice.id
                            ? "bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500"
                            : isDark
                              ? "bg-slate-900 border-slate-600"
                              : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <span className="text-lg">{voice.icon}</span>
                        <div className={`text-xs font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{voice.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Voicemail Script (for voicemail mode) */}
            {selectedMode === "voicemail" && (
              <div className={`rounded-xl p-4 border ${
                isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"
              }`}>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                  Voicemail Script
                </label>
                <textarea
                  value={voicemailScript}
                  onChange={(e) => setVoicemailScript(e.target.value)}
                  disabled={isActive}
                  rows={4}
                  placeholder="Enter your voicemail message..."
                  className={`w-full p-3 rounded-lg text-sm outline-none border resize-none transition-all disabled:opacity-50 ${
                    isDark
                      ? "bg-slate-900 border-slate-600 focus:border-emerald-500 text-white"
                      : "bg-gray-50 border-gray-200 focus:border-emerald-500"
                  }`}
                />
              </div>
            )}

            {/* Prompt Editor Toggle */}
            <button
              onClick={() => setShowPromptEditor(!showPromptEditor)}
              className={`w-full p-3 rounded-lg text-sm font-medium text-left transition-all border flex items-center justify-between ${
                isDark
                  ? "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <FaCog className={isDark ? "text-slate-400" : "text-gray-500"} />
                Customize AI Prompt
              </span>
              {showPromptEditor ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {showPromptEditor && (
              <div className={`rounded-xl p-4 border ${
                isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"
              }`}>
                <textarea
                  value={customPrompt || DEFAULT_PROMPTS[selectedMode] || DEFAULT_PROMPTS.realtime}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  disabled={isActive}
                  rows={6}
                  className={`w-full p-3 rounded-lg text-xs font-mono outline-none border resize-none transition-all disabled:opacity-50 ${
                    isDark
                      ? "bg-slate-900 border-slate-600 focus:border-emerald-500 text-white"
                      : "bg-gray-50 border-gray-200 focus:border-emerald-500"
                  }`}
                />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className={`p-3 rounded-lg text-sm ${
                isDark ? "bg-red-500/20 border border-red-500/30 text-red-400" : "bg-red-50 border border-red-200 text-red-600"
              }`}>
                ❌ {error}
              </div>
            )}

            {/* Action Button */}
            {selectedMode === "browser" ? (
              // Browser Voice Controls
              !isConnected ? (
                <button
                  onClick={connectBrowserVoice}
                  disabled={isConnecting || PROVIDERS[selectedProvider]?.disabled}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold hover:shadow-xl hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100 transition-all flex items-center justify-center gap-3"
                >
                  {isConnecting ? (
                    <><FaSpinner className="animate-spin" /> Connecting...</>
                  ) : (
                    <><FaMicrophone /> Start Session</>
                  )}
                </button>
              ) : (
                <div className={`p-4 rounded-xl border ${
                  isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-50 border-emerald-200"
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center animate-pulse">
                        <FaMicrophone className="text-white" />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>Session Active</p>
                        <p className={`text-xs font-mono ${isDark ? "text-emerald-500" : "text-emerald-600"}`}>{formatTime(sessionTime)}</p>
                      </div>
                    </div>
                    <button
                      onClick={disconnectBrowserVoice}
                      className="px-3 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-2"
                    >
                      <FaPhoneSlash /> End
                    </button>
                  </div>
                  <div className={`flex items-center gap-2 text-xs ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                    <FaHandPaper />
                    <span>Interrupt enabled - speak to interrupt AI</span>
                  </div>
                </div>
              )
            ) : (
              // Phone Call Controls
              !activeCall ? (
                <button
                  onClick={startPhoneCall}
                  disabled={isLoading || !phoneNumber || PROVIDERS[selectedProvider]?.disabled}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold hover:shadow-xl hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100 transition-all flex items-center justify-center gap-3"
                >
                  {isLoading ? (
                    <><FaSpinner className="animate-spin" /> Calling...</>
                  ) : (
                    <><FaPlay /> Start Call</>
                  )}
                </button>
              ) : (
                <div className={`p-4 rounded-xl border ${
                  isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-50 border-emerald-200"
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center animate-pulse">
                        <FaMicrophone className="text-white" />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>Call Active</p>
                        <p className={`text-xs font-mono ${isDark ? "text-emerald-500" : "text-emerald-600"}`}>{formatTime(callDuration)}</p>
                      </div>
                    </div>
                    <button
                      onClick={endPhoneCall}
                      className="px-3 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-2"
                    >
                      <FaPhoneSlash /> End
                    </button>
                  </div>
                  <div className={`flex items-center gap-2 text-xs ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                    <FaHandPaper />
                    <span>Interrupt enabled - speak to interrupt AI</span>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Right: Live Transcript/Conversation (3 cols) */}
          <div className={`lg:col-span-3 rounded-xl border overflow-hidden ${
            isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"
          }`}>
            {/* Header */}
            <div className={`px-4 py-3 border-b flex items-center justify-between ${
              isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
            }`}>
              <div className="flex items-center gap-2">
                <FaFileAlt className={isDark ? "text-slate-400" : "text-gray-500"} />
                <span className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}>
                  {selectedMode === "browser" ? "Live Conversation" : "Call Transcript"}
                </span>
              </div>
              {isActive && (
                <div className="flex items-center gap-3">
                  {isAiSpeaking && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isDark ? "bg-orange-500/20 text-orange-400" : "bg-orange-100 text-orange-600"
                    }`}>
                      AI Speaking...
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-xs text-emerald-500">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    {selectedMode === "browser" ? "Live" : "Recording"}
                  </span>
                </div>
              )}
            </div>

            {/* Conversation Content */}
            <div className={`h-[450px] overflow-y-auto p-4 space-y-3 ${isDark ? "" : "bg-gray-50/50"}`}>
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  {selectedMode === "browser" ? (
                    <>
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
                        isDark ? "bg-emerald-500/20" : "bg-emerald-100"
                      }`}>
                        <FaMicrophone className={`text-3xl ${isDark ? "text-emerald-400" : "text-emerald-500"}`} />
                      </div>
                      <h3 className={`font-semibold text-lg mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                        Ready to Connect
                      </h3>
                      <p className={`text-sm max-w-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        Click "Start Session" to begin. Speak naturally - you can interrupt the AI anytime!
                      </p>
                    </>
                  ) : (
                    <>
                      <FaComments className={`text-4xl mb-3 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
                      <p className={`text-sm ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                        Conversation transcript will appear here
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl px-4 py-2.5 ${
                          msg.role === "user"
                            ? isDark
                              ? "bg-emerald-500/20 border border-emerald-500/30"
                              : "bg-emerald-500 text-white"
                            : msg.role === "assistant"
                            ? isDark
                              ? "bg-slate-700/50 border border-slate-600/50"
                              : "bg-white border border-gray-200 shadow-sm"
                            : isDark
                            ? "bg-slate-700/30 border border-slate-600/30"
                            : "bg-gray-100 border border-gray-200"
                        }`}
                      >
                        {msg.role !== "system" && (
                          <div className="flex items-center gap-2 mb-1">
                            {msg.role === "user" ? (
                              <FaMicrophone className={`text-xs ${isDark ? "text-emerald-400" : "text-emerald-200"}`} />
                            ) : (
                              <FaRobot className={`text-xs ${isDark ? "text-orange-400" : "text-orange-500"}`} />
                            )}
                            <span className={`text-xs font-medium ${
                              msg.role === "user" 
                                ? isDark ? "text-emerald-400" : "text-emerald-200"
                                : isDark ? "text-orange-400" : "text-orange-600"
                            }`}>
                              {msg.role === "user" ? "You" : "AI Assistant"}
                            </span>
                            {msg.timestamp && (
                              <span className={`text-[10px] ${isDark ? "text-slate-600" : "text-gray-400"}`}>
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                        )}
                        <p className={`text-sm ${msg.role === "user" && !isDark ? "text-white" : ""}`}>{msg.text}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Browser Voice Status Bar */}
            {selectedMode === "browser" && isConnected && (
              <div className={`px-6 py-4 border-t ${
                isDark ? "bg-slate-900/50 border-slate-700" : "bg-white border-gray-200"
              }`}>
                <div className="flex items-center justify-center gap-6">
                  {/* User indicator */}
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                        isSpeaking
                          ? isDark ? "bg-emerald-500/30 border-2 border-emerald-400" : "bg-emerald-100 border-2 border-emerald-400"
                          : isDark ? "bg-slate-700/50 border-2 border-slate-600" : "bg-gray-100 border-2 border-gray-300"
                      }`}
                      style={{ transform: isSpeaking ? `scale(${1 + audioLevel * 0.2})` : "scale(1)" }}
                    >
                      {isSpeaking ? (
                        <FaMicrophone className={`text-xl ${isDark ? "text-emerald-400" : "text-emerald-500"}`} />
                      ) : (
                        <FaMicrophoneSlash className={`text-xl ${isDark ? "text-slate-400" : "text-gray-400"}`} />
                      )}
                    </div>
                    <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>You</span>
                  </div>

                  {/* AI indicator */}
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                        isAiSpeaking
                          ? isDark ? "bg-orange-500/30 border-2 border-orange-400 animate-pulse" : "bg-orange-100 border-2 border-orange-400 animate-pulse"
                          : isDark ? "bg-slate-700/50 border-2 border-slate-600" : "bg-gray-100 border-2 border-gray-300"
                      }`}
                    >
                      {isAiSpeaking ? (
                        <FaVolumeUp className={`text-xl ${isDark ? "text-orange-400" : "text-orange-500"}`} />
                      ) : (
                        <FaRobot className={`text-xl ${isDark ? "text-slate-400" : "text-gray-400"}`} />
                      )}
                    </div>
                    <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>AI</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status */}
        {callStatus && !activeCall && selectedMode !== "browser" && (
          <div className={`mt-4 p-3 rounded-lg text-sm text-center ${
            callStatus.includes("✅") 
              ? isDark ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-600"
              : isDark ? "bg-blue-500/20 border border-blue-500/30 text-blue-400" : "bg-blue-50 border border-blue-200 text-blue-600"
          }`}>
            {callStatus}
          </div>
        )}

        {/* Tip */}
        <div className={`mt-6 text-center text-sm ${isDark ? "text-slate-500" : "text-gray-500"}`}>
          💡 Speak naturally. <strong>Interrupt anytime</strong> - the AI will stop and listen!
        </div>
      </div>
    </div>
  );
};

RealTimeVoice.propTypes = {
  selectedTeam: PropTypes.shape({
    id: PropTypes.string.isRequired,
    teamName: PropTypes.string,
  }),
};

export default RealTimeVoice;
